import Numeral from 'numeral';
import dayjs from 'dayjs';

/**
 * connect string by multiple arguments
 * @param {*} args
 * @param {String} gapValue
 * @returns {String}
 */
export const connectString = (...args) => (gapValue) => {
	return args.filter((v) => v !== null && v !== undefined).join(gapValue);
};

/**
 * set default value
 * @param {*} originValue
 * @param {*} replaceValue
 * @returns {String}
 */
export const defaultValue = (originValue, replaceValue = '-') => {
	return typeof (originValue) === 'undefined' || originValue === null || originValue === ''
		? replaceValue
		: originValue;
};

/**
 * check value is empty
 * @param {*} value
 * @returns {Boolean}
 */
export const isEmpty = (value = null) => {
	return value === null || value === '' || value === undefined;
};

/**
 * Use for get path of object and if it invalid it's return default value
 * @param {*} defaultVal
 * @param {Array} path
 * @param {Object} data
 */
export const pathOr = (defaultVal, path = []) => (data = {}) => {
	// Cache the current object
	let current = data;

	if (path.length === 0) {
		return defaultVal;
	}

	// For each item in the path, dig into the object
	for (let i = 0; i < path.length; i++) {
		// If the item isn't found, return the default (or null)
		if (isEmpty(current) || isEmpty(current[path[i]])) {
			return defaultVal;
		}

		// Otherwise, update the current value
		current = current[path[i]];
	}

	return current;
};

/**
 * randomSearchString
 * example output - "AH07"
 * @returns {String}
 */
export const randomSearchString = () => Math.random().toString(36).substring(2, 6).toUpperCase();

/**
 * cleanObject (delete null property from object)
 * @param {Object} Object input
 * @returns {cleanObject}
 */
export const cleanObject = (object) => {
	Object.keys(object).forEach((key) => (object[key] === null) && delete object[key]);

	return object;
};

/**
 * cloning an object
 * @param {Object} object input
 * @returns {Object} cloned object
 */
export const cloneDeep = (object) => {
	return JSON.parse(JSON.stringify(object));
};

export const numberFormat = (value, format = '0,0') => {
	return Numeral(value).format(format);
};

/**
 * replace value
 * @param {*} value
 * @param {Array} options
 * @returns {*} new value
 */
export const replaceTo = (value = null, options = []) => {
	return options.reduce((acc, cur) => {
		if (value === cur.from) {
			return cur.to;
		}
		return acc;
	}, value);
};

/**
 * dateFormat
 * @param {String} Datetime value
 * @param {String} format
 * @returns {dateFormat}
 */
export const dateFormat = (value, format = 'YYYY-MM-DD') => {
	if (!value) {
		return '';
	}

	return dayjs(value).format(format);
};

/**
 * getBooleanQueryParameter
 * @param {String} value
 */
export const getBooleanQueryParameter = (value) => {
	const numVal = Number(value);
	return [0, 1].includes(numVal) ? numVal : null;
};

/**
 * get selected value, use with BaseDropDown components
 * @param {Object} selectedValue
 * @param {Object} options
 */
export const getSelectedValueDropdown = (selectedValue = null, options = {}) => {
	const values = Object.values(options);
	if (values.length > 0) {
		const findValue = ({ value }) => value === selectedValue;
		return values.find(findValue);
	}
	return { name: null, value: null };
};

/**
 * hasSearchFilter
 * @param {Object} Object of query params
 * @param {Array} Array of query params which will exclude from checking
 * @returns {Boolean}
 */
export const hasSearchFilter = (queryParams = {}, exclude = []) => {
	const query = cleanObject(cloneDeep(queryParams));
	let excludedParams = ['r', 'q', 'sort_by', 'sort_direction'];
	if (Array.isArray(exclude) && exclude.length) {
		excludedParams = [...excludedParams, ...exclude];
	}
	return Object.keys(query).filter((key) => !excludedParams.includes(key)).some((k) => query[k] !== null);
};

/**
 * datetimeFormat
 * @param {String} Datetime value
 * @param {String} format
 * @returns {datetimeFormat}
 */
export const datetimeFormat = (value, format = 'DD MMM YYYY, HH:mm') => {
	if (!value) {
		return '';
	}

	return dayjs(value).format(format);
};
